/* eslint-disable max-lines */

import { connect } from 'react-redux';

import Header from 'Component/Header/Header.component';
import {Page} from 'Component/Header/Header.config';
import {HeaderContainerFunctions, HeaderContainerProps} from 'Component/Header/Header.type';
import {CUSTOMER_ACCOUNT_OVERLAY_KEY} from 'Component/MyAccountOverlay/MyAccountOverlay.config';
import {CheckoutStepUrl} from 'Route/Checkout/Checkout.config';
import {UrlRewritePageType} from 'Route/UrlRewrites/UrlRewrites.config';
import {
    DEFAULT_HEADER_STATE,
    HeaderContainer as SourceHeaderContainer,
    mapDispatchToProps,
    mapStateToProps,
} from 'SourceComponent/Header/Header.container';
import {ReactElement} from 'Type/Common.type';
import history from 'Util/History';
import {debounce} from 'Util/Request/Debounce';

export {
    DEFAULT_HEADER_STATE,
};

export {
    mapStateToProps,
    mapDispatchToProps,
};

/** @namespace Steinkrueger/Component/Header/Container */
export class HeaderContainer extends SourceHeaderContainer {
    containerFunctions: HeaderContainerFunctions = {
        onBackButtonClick: this.onBackButtonClick.bind(this),
        onCloseButtonClick: this.onCloseButtonClick.bind(this),
        onSearchBarFocus: this.onSearchBarFocus.bind(this),
        onClearSearchButtonClick: this.onClearSearchButtonClick.bind(this),
        onMyAccountButtonClick: this.onMyAccountButtonClick.bind(this),
        onSearchBarChange: this.onSearchBarChange.bind(this),
        onEditButtonClick: this.onEditButtonClick.bind(this),
        onMinicartButtonClick: this.onMinicartButtonClick.bind(this),
        onOkButtonClick: this.onOkButtonClick.bind(this),
        onCancelButtonClick: this.onCancelButtonClick.bind(this),
        onSearchOutsideClick: this.onSearchOutsideClick.bind(this),
        onMyAccountOutsideClick: this.onMyAccountOutsideClick.bind(this),
        onMinicartOutsideClick: this.onMinicartOutsideClick.bind(this),
        onSignIn: this.onSignIn.bind(this),
        shareWishlist: this.shareWishlist.bind(this),
        hideActiveOverlay: this.props.hideActiveOverlay,
        onDesktopMenuButtonClick: this.onDesktopMenuButtonClick.bind(this),
        onDesktopMenuOutsideClick: this.onDesktopMenuOutsideClick.bind(this),
        onAvailableDateMenuClick: this.onAvailableDateMenuClick.bind(this),
        onAvailableDateMenuOutsideClick: this.onAvailableDateMenuOutsideClick.bind(this),
    };

    scrollPosition = 0;

    __construct(props: HeaderContainerProps): void {
        super.__construct?.(props);

        this.state = {
            prevPathname: '',
            searchCriteria: '',
            isClearEnabled: this.getIsClearEnabled(),
            showMyAccountLogin: false,
            isSticky: false,
            isAvailableDateMenuOpened: false,
            isPLP: (window.actionName.type === UrlRewritePageType.CATEGORY) || location.pathname.includes('search'),
        };
    }

    componentDidMount(): void {
        const SCROLL_DEBOUNCE_DELAY = 10;

        window.addEventListener(
            'scroll',
            debounce(this.handleScroll.bind(this), SCROLL_DEBOUNCE_DELAY) as EventListenerOrEventListenerObject,
        );

        this.handleHeaderVisibility();
        super.componentDidMount();

        history.listen((location) => {
            const isPLP = (!!location.state && !!location.state.category) || location.pathname.includes('search');
            this.setState({ isPLP });
        });
    }

    componentDidUpdate(prevProps: HeaderContainerProps): void {
        this.hideSearchOnStateChange(prevProps);
        this.handleHeaderVisibility();
    }

    handleScroll(): void {
        const windowY = window.pageYOffset;

        this.handleNavVisibilityOnScroll(windowY);
        this.scrollPosition = windowY;
    }

    onMyAccountButtonClick(): void {
        const {
            showOverlay,
            setNavigationState,
        } = this.props;

        this.setState({ showMyAccountLogin: true });

        showOverlay(CUSTOMER_ACCOUNT_OVERLAY_KEY);
        setNavigationState({
            name: Page.CHECKOUT_ACCOUNT,
            title: 'Sign in',
            onCloseClick: this.closeOverlay,
        });
    }

    onDesktopMenuButtonClick(): void {
        const { showOverlay, setNavigationState, navigationState: { name } } = this.props;

        if (name === 'DesktopMenu') {
            this.setState(() => {
                setNavigationState({
                    name: '',
                });
            });
            this.closeOverlay();
        } else {
            showOverlay('desktop_menu');
            this.setState(() => {
                setNavigationState({
                    name: 'DesktopMenu',
                });
            });
        }
    }

    onDesktopMenuOutsideClick(): void {
        const {
            navigationState: { name },
            device,
            setNavigationState,
        } = this.props;

        if (device.isMobile
            || (name !== 'DesktopMenu')
        ) {
            return;
        }

        this.setState(() => {
            setNavigationState({
                name: '',
            });
        });

        this.closeOverlay();
    }

    onAvailableDateMenuOutsideClick(): void {
        this.setState({isAvailableDateMenuOpened: false});
        this.closeOverlay();
    }

    onAvailableDateMenuClick(): void {
        this.setState({isAvailableDateMenuOpened: !this.state.isAvailableDateMenuOpened});
    }

    handleNavVisibilityOnScroll(windowY: number): void {
        const { device } = this.props;
        const ERROR_TOP_OFFSET = 10;
        const TOP_MIN_OFFSET = 70;

        if (device.isMobile) {
            return;
        }

        if (windowY < TOP_MIN_OFFSET) {
            this.setState({isSticky: false});

            return;
        }

        if (Math.abs(windowY - this.scrollPosition) < ERROR_TOP_OFFSET) {
            return;
        }

        if (windowY >= this.scrollPosition) {
            this.setState({isSticky: true});
        }
    }

    containerProps() {
        const {
            activeOverlay,
            navigationState,
            cartTotals,
            compareTotals,
            Loading,
            header_logo_src,
            logo_alt,
            logo_height,
            logo_width,
            isLoading,
            device,
            isWishlistLoading,
            productsInWishlist,
            isMobile,
        } = this.props;

        const {
            isClearEnabled,
            searchCriteria,
            showMyAccountLogin,
            shouldRenderCartOverlay,
            isAvailableDateMenuOpened,
            isPLP,
        } = this.state;

        const {
            location: {
                pathname,
            },
        } = history;

        const isCheckout = pathname.includes(CheckoutStepUrl.CHECKOUT_URL);
        const isDesktopMenuOpened = navigationState.name === 'DesktopMenu';

        return {
            activeOverlay,
            navigationState,
            cartTotals,
            compareTotals,
            Loading,
            header_logo_src,
            logo_alt,
            logo_height,
            logo_width,
            isLoading,
            isClearEnabled,
            searchCriteria,
            isCheckout,
            showMyAccountLogin,
            device,
            isWishlistLoading,
            productsInWishlist,
            shouldRenderCartOverlay,
            firstname: this.getUserName(),
            isMobile,
            isDesktopMenuOpened,
            isAvailableDateMenuOpened,
            isPLP,
        };
    }

    render(): ReactElement {
        const { isSticky } = this.state;

        return (
            <Header
              { ...{ isSticky } }
              { ...this.containerProps() }
              { ...this.containerFunctions }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(HeaderContainer);
